/* Client preferences — persisted to localStorage, applied to <html>. Real user
   settings only (theme/motion/sound/etc.); no app state masquerading as prefs. */
import { defineStore } from 'pinia'
import { DEFAULT_AUDIO, normalizeAudio, normalizeParticipants, type AudioSettings, type ParticipantAudio } from '@/lib/audio-settings'

const LS_KEY = 'atrium.prefs.v1'

export type Theme = 'dusk' | 'dawn'
export type Motion = 'full' | 'calm'
// Screen-share quality. 'auto' sends a single source-resolution layer for sharp
// text and sheds framerate (not clarity) under congestion; the rest are explicit.
export type ScreenQuality = 'auto' | 'sharp' | 'p1080' | 'p720' | 'motion'

interface PrefsState {
  theme: Theme
  motion: Motion
  pebbles: boolean
  lowBandwidth: boolean
  sensory: number // 0..1
  sfx: boolean // UI sound effects (join/leave/mute/knock/chat…)
  sfxVolume: number // 0..1
  dev: boolean // developer mode — unlocks the dev panel overlay
  micId: string // call devices ('' = browser default)
  camId: string
  speakerId: string
  callAudio: AudioSettings
  participantAudio: Record<string, ParticipantAudio>
  screenQuality: ScreenQuality
  hiddenBoards: string[] // board ids hidden from the tasks board switcher
}

const defaults: PrefsState = {
  theme: 'dusk',
  motion: 'full',
  pebbles: true,
  lowBandwidth: false,
  sensory: 1,
  sfx: true,
  sfxVolume: 0.7,
  dev: false,
  micId: '',
  camId: '',
  speakerId: '',
  callAudio: { ...DEFAULT_AUDIO },
  participantAudio: {},
  screenQuality: 'auto',
  hiddenBoards: [],
}

function loadSaved(): Partial<PrefsState> {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '{}') as Partial<PrefsState>
  } catch {
    return {}
  }
}

export const usePrefs = defineStore('prefs', {
  state: (): PrefsState => {
    const saved = loadSaved()
    return { ...defaults, ...saved, callAudio: normalizeAudio(saved?.callAudio), participantAudio: normalizeParticipants(saved?.participantAudio) }
  },
  actions: {
    setCallAudio(patch: Partial<AudioSettings>) {
      this.callAudio = normalizeAudio({ ...this.callAudio, ...patch })
      this.persist()
    },
    setParticipantAudio(id: string, patch: Partial<ParticipantAudio>) {
      if (!id.startsWith('@')) return
      this.participantAudio = normalizeParticipants({ ...this.participantAudio, [id]: { ...(this.participantAudio[id] ?? { volume: 100, muted: false }), ...patch } })
      this.persist()
    },
    resetCallAudio() {
      this.callAudio = { ...DEFAULT_AUDIO }
      this.persist()
    },
    persist() {
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(this.$state))
      } catch {
        /* non-fatal */
      }
    },
    apply() {
      const root = document.documentElement
      root.setAttribute('data-theme', this.theme === 'dawn' ? 'dawn' : 'dusk')
      root.setAttribute('data-motion', this.motion === 'calm' ? 'calm' : 'full')
      root.style.setProperty('--sensory', String(this.sensory))
    },
    set<K extends keyof PrefsState>(key: K, value: PrefsState[K]) {
      this.$state[key] = value
      this.persist()
      this.apply()
    },
    toggle(key: 'pebbles' | 'lowBandwidth' | 'sfx' | 'dev') {
      this.set(key, !this.$state[key])
    },
  },
})
